import { useState } from 'react';
import { useApi } from '../api/ApiProvider';
import type { DocumentSummary } from '../api/types';
import { Button } from './ui/Button';
import { ReasonDialog } from './ReasonDialog';
import { ErrorBanner } from './ui/ErrorBanner';

type Pending = 'remove' | 'hold' | null;

/**
 * Hide / unhide / remove / legal hold for one document, rendered as a row of small buttons.
 * Only actions in `doc.actions` appear; the API enforces them again.
 */
export function RecordsActions({
  doc,
  onChanged,
  compact = false,
}: {
  doc: DocumentSummary;
  onChanged?: () => void;
  compact?: boolean;
}) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<Pending>(null);
  const [error, setError] = useState<Error | null>(null);

  const can = (a: string) => doc.actions.includes(a as DocumentSummary['actions'][number]);
  const showHide = !doc.hidden && can('hide');
  const showUnhide = doc.hidden && can('unhide');
  const showRemove = can('remove') && doc.status !== 'purged';
  const showHold = can('hold');
  if (!showHide && !showUnhide && !showRemove && !showHold) return null;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  const size = compact ? 'sm' : 'md';

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {showHide ? (
          <Button variant="secondary" size={size} icon="eye-off" busy={busy} onClick={() => void run(() => api.documents.hide(doc.id))}>
            Hide
          </Button>
        ) : null}
        {showUnhide ? (
          <Button variant="secondary" size={size} icon="eye" busy={busy} onClick={() => void run(() => api.documents.unhide(doc.id))}>
            Unhide
          </Button>
        ) : null}
        {showHold ? (
          <Button
            variant="secondary"
            size={size}
            icon="shield"
            busy={busy}
            onClick={() => {
              setDialog('hold');
            }}
          >
            {doc.legalHold ? 'Release hold' : 'Legal hold'}
          </Button>
        ) : null}
        {showRemove ? (
          <Button
            variant="secondary"
            size={size}
            icon="x"
            busy={busy}
            onClick={() => {
              setDialog('remove');
            }}
          >
            Remove
          </Button>
        ) : null}
      </div>
      {error ? (
        <div className="mt-2">
          <ErrorBanner error={error} />
        </div>
      ) : null}

      {dialog ? (
        <ReasonDialog
          doc={doc}
          kind={dialog}
          open
          onClose={() => setDialog(null)}
          onSubmit={async (reason) => {
            await (dialog === 'remove' ? api.documents.remove(doc.id, reason) : api.documents.hold(doc.id, !doc.legalHold, reason));
            onChanged?.();
          }}
        />
      ) : null}
    </div>
  );
}
